import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import {
	Alert,
	Button,
	ColumnLayout,
	Container,
	Form,
	FormField,
	Header,
	Select,
	SpaceBetween
} from '@cloudscape-design/components'
import useApplications from '../../../hooks/query/useApplications'
import useMongoDBApplication from '../../../hooks/query/useMongoDBApplication'
import useMongoDBEligibility from '../../../hooks/query/useMongoDBEligibility'
import useCreateNewMongoDB from '../../../hooks/mutate/useCreateNewMongoDB'
import {
	getMongoDBClusterFormValues,
	mongodbClusterResolver,
	prepareMongoDBCluster
} from '../../../helper/mongodbClusterConfig'
import type { MongoDBClusterFormValues } from '../../../helper/mongodbClusterConfig'
import {
	buildMongoDBApplicationDetails,
	getMongoDBDisplayValue
} from '../../../helper/mongodbApplicationDetails'
import { normalizeApplicationEnvironment } from '../../../helper/gcpAliasValidation'
import ApplicationDetails from '../../../components/MongoDB/ApplicationDetails'
import ClusterConfiguration from '../../../components/MongoDB/ClusterConfiguration'
import { getMongoDBErrorMessage } from '../../../components/MongoDB/RequestError'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { isMongoDBApplicationEligible } from '../../../helper/mongodbApplicationEligibility'

const environmentLabels: Record<string, string> = {
	dev: 'Development',
	staging: 'Staging',
	prod: 'Production'
}

const NewMongoDBRequest: FC = () => {
	const navigate = useNavigate()
	const { id: applicationID } = useParams<{ id: string }>()
	const isApplicationRoute = Boolean(applicationID)
	const [submissionError, setSubmissionError] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const clusterForm = useForm<MongoDBClusterFormValues>({
		defaultValues: getMongoDBClusterFormValues(),
		resolver: mongodbClusterResolver
	})
	const [showForm, setShowForm] = useState(false)
	const [selectedEnvironment, setSelectedEnvironment] = useState('')
	const [selectedApplicationId, setSelectedApplicationId] = useState('')
	const currentApplicationId = applicationID ?? selectedApplicationId
	const eligibilityQuery = useMongoDBEligibility(currentApplicationId)
	const createMutation = useCreateNewMongoDB()
	const [environmentError, setEnvironmentError] = useState('')
	const [applicationError, setApplicationError] = useState('')
	const applicationsQuery = useApplications(!isApplicationRoute)
	const applicationQuery = useMongoDBApplication(currentApplicationId)

	const approvedApplications = (
		isApplicationRoute
			? applicationQuery.data
				? [applicationQuery.data]
				: []
			: applicationsQuery.data ?? []
	).filter(isMongoDBApplicationEligible)

	const environmentOptions = Array.from(
		new Set(
			approvedApplications.map((application) =>
				normalizeApplicationEnvironment(application.details?.environment)
			)
		)
	)
		.filter(Boolean)
		.map((environment) => ({
			label: environmentLabels[environment] || environment,
			value: environment
		}))
	const currentEnvironment = isApplicationRoute
		? normalizeApplicationEnvironment(
				applicationQuery.data?.details?.environment
		  )
		: selectedEnvironment
	const applicationOptions = approvedApplications
		.filter(
			(application) =>
				Boolean(currentEnvironment) &&
				normalizeApplicationEnvironment(application.details?.environment) ===
					currentEnvironment
		)
		.map((application) => ({
			label:
				getMongoDBDisplayValue(application.details?.applicationName) ||
				application.applicationID,
			value: application.applicationID,
			description: application.applicationID
		}))
	const applicationOptionsStatus = isApplicationRoute
		? applicationQuery.isError
			? 'error'
			: 'finished'
		: applicationsQuery.isLoading
		? 'loading'
		: applicationsQuery.isError
		? 'error'
		: 'finished'
	const applicationCandidate =
		applicationQuery.data?.applicationID === currentApplicationId
			? applicationQuery.data
			: approvedApplications.find(
					(application) => application.applicationID === currentApplicationId
			  )

	const selectedApplication =
		isMongoDBApplicationEligible(applicationCandidate) &&
		normalizeApplicationEnvironment(
			applicationCandidate.details?.environment
		) === currentEnvironment
			? applicationCandidate
			: undefined
	const inheritedDetails = selectedApplication
		? buildMongoDBApplicationDetails(selectedApplication)
		: undefined
	const isInheritedLoading =
		Boolean(currentApplicationId) && applicationQuery.isFetching

	const handleSubmit = async () => {
		if (isSubmitting || isInheritedLoading || applicationQuery.isError) return
		setEnvironmentError('')
		setApplicationError('')
		setSubmissionError('')
		if (!currentEnvironment) {
			setEnvironmentError(
				'Select an environment with an approved Application that has MongoDB enabled as an add-on.'
			)
			return
		}
		const isClusterValid = await clusterForm.trigger()
		const application = applicationQuery.data
		if (
			!isMongoDBApplicationEligible(application) ||
			application.applicationID !== currentApplicationId ||
			normalizeApplicationEnvironment(application.details?.environment) !==
				currentEnvironment
		) {
			setApplicationError(
				'Select an approved Application with MongoDB enabled as an add-on and wait for its details to load.'
			)
			return
		}
		const details = buildMongoDBApplicationDetails(application)
		if (
			!details.applicationName ||
			!details.environment ||
			!details.projectId
		) {
			setApplicationError(
				'The selected Application must have a name, environment and GCP Project ID.'
			)
			return
		}
		if (!isClusterValid) return
		const result = prepareMongoDBCluster(clusterForm.getValues())
		if (result.status === 'invalid') return
		setIsSubmitting(true)
		try {
			const checked = await eligibilityQuery.refetch()
			if (checked.isError) throw checked.error
			if (!checked.data?.eligible) {
				throw new Error(
					checked.data?.message || 'The selected Application is not eligible.'
				)
			}
			await createMutation.mutateAsync({
				action: 'CREATE',
				details: {
					applicationID: application.applicationID,
					cluster: result.cluster
				}
			})
			navigate('/mongodb', {
				state: {
					notice:
						'Request submitted. It may take a few moments to appear. Refresh the list to check.'
				}
			})
		} catch (error) {
			setSubmissionError(getMongoDBErrorMessage(error))
		} finally {
			setIsSubmitting(false)
		}
	}

	if (isApplicationRoute && applicationQuery.isLoading)
		return <LoadingSpinner />
	if (
		isApplicationRoute &&
		applicationQuery.data &&
		!isMongoDBApplicationEligible(applicationQuery.data)
	) {
		return <Navigate to={`/applications/${applicationID}`} replace />
	}

	return (
		<SpaceBetween direction="vertical" size="l">
			{!isApplicationRoute && !showForm ? (
				<SpaceBetween direction="vertical" size="m">
					<Header variant="h2">
						Do you already have an application onboarded with MongoDB as an
						add-on?
					</Header>

					<Alert type="info" header="Before you continue">
						Make sure you selected the MongoDB checkbox in the Add-ons section
						when submitting your Application onboarding request. The Application
						must be approved before you can onboard a MongoDB workload.
					</Alert>
					<SpaceBetween direction="horizontal" size="xs">
						<Button variant="primary" onClick={() => setShowForm(true)}>
							Yes
						</Button>
						<Button onClick={() => navigate('/applications')}>No</Button>
					</SpaceBetween>
				</SpaceBetween>
			) : (
				<Form
					header={<Header variant="h1">MongoDB Onboarding</Header>}
					actions={
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								disabled={isSubmitting}
								onClick={() =>
									navigate(
										isApplicationRoute
											? `/applications/${applicationID}`
											: '/mongodb'
									)
								}
							>
								Cancel
							</Button>
							{!isApplicationRoute && (
								<Button
									disabled={isSubmitting}
									onClick={() => {
										setShowForm(false)
										setSelectedEnvironment('')
										setSelectedApplicationId('')
										setEnvironmentError('')
										setApplicationError('')
										clusterForm.reset()
										setSubmissionError('')
									}}
								>
									Back
								</Button>
							)}
							<Button
								variant="primary"
								loading={isSubmitting}
								disabled={
									isSubmitting ||
									(!isApplicationRoute && applicationsQuery.isLoading) ||
									(!isApplicationRoute && applicationsQuery.isError) ||
									isInheritedLoading ||
									applicationQuery.isError ||
									!eligibilityQuery.data?.eligible ||
									eligibilityQuery.isFetching
								}
								onClick={() => void handleSubmit()}
							>
								Submit request
							</Button>
						</SpaceBetween>
					}
				>
					<SpaceBetween direction="vertical" size="l">
						{submissionError && (
							<Alert type="error" header="Unable to submit request">
								{submissionError}
							</Alert>
						)}
						<Container header={<Header variant="h2">MongoDB Request</Header>}>
							<SpaceBetween direction="vertical" size="m">
								<ColumnLayout columns={2}>
									<FormField
										label="Environment"
										description={
											isApplicationRoute
												? 'Environment of the approved Application with MongoDB enabled.'
												: 'Select an environment with an approved Application that has MongoDB enabled as an add-on.'
										}
										errorText={environmentError || undefined}
									>
										<Select
											selectedOption={
												environmentOptions.find(
													(option) => option.value === currentEnvironment
												) ?? null
											}
											options={environmentOptions}
											placeholder="Select Environment"
											statusType={applicationOptionsStatus}
											disabled={
												isSubmitting ||
												isApplicationRoute ||
												applicationsQuery.isLoading ||
												applicationsQuery.isError ||
												environmentOptions.length === 0
											}
											onChange={({ detail }) => {
												setSelectedEnvironment(
													detail.selectedOption.value || ''
												)
												setSelectedApplicationId('')
												setEnvironmentError('')
												setApplicationError('')
												clusterForm.clearErrors()
												setSubmissionError('')
											}}
										/>
									</FormField>
									<FormField
										label="Application Name"
										description={
											isApplicationRoute
												? 'Approved Application with MongoDB enabled as an add-on.'
												: 'Only approved Applications with MongoDB enabled as an add-on are listed.'
										}
										errorText={applicationError || undefined}
									>
										<Select
											selectedOption={
												applicationOptions.find(
													(option) => option.value === currentApplicationId
												) ?? null
											}
											options={applicationOptions}
											placeholder={
												currentEnvironment
													? 'Select Application'
													: 'Select an environment first'
											}
											filteringType="auto"
											statusType={applicationOptionsStatus}
											disabled={
												isSubmitting ||
												isApplicationRoute ||
												!currentEnvironment ||
												applicationsQuery.isLoading ||
												applicationsQuery.isError ||
												applicationOptions.length === 0
											}
											onChange={({ detail }) => {
												setSelectedApplicationId(
													detail.selectedOption.value || ''
												)
												setApplicationError('')
												clusterForm.clearErrors()
												setSubmissionError('')
											}}
										/>
									</FormField>
								</ColumnLayout>
								{!isApplicationRoute && applicationsQuery.isError && (
									<Alert type="error">
										<SpaceBetween direction="vertical" size="s">
											<span>Unable to load Applications.</span>
											<Button onClick={() => void applicationsQuery.refetch()}>
												Retry
											</Button>
										</SpaceBetween>
									</Alert>
								)}
								{!isApplicationRoute &&
									!applicationsQuery.isLoading &&
									!applicationsQuery.isError &&
									!approvedApplications.length && (
										<Alert type="info">
											No approved Applications with MongoDB enabled as an add-on
											are available. Select MongoDB during Application
											onboarding and wait for the Application to be approved.
										</Alert>
									)}
							</SpaceBetween>
						</Container>
						{currentApplicationId && eligibilityQuery.isFetching && (
							<Alert type="info">Checking Application eligibility.</Alert>
						)}
						{currentApplicationId && eligibilityQuery.isError && (
							<Alert type="error">
								<SpaceBetween direction="vertical" size="s">
									<span>{getMongoDBErrorMessage(eligibilityQuery.error)}</span>
									<Button onClick={() => void eligibilityQuery.refetch()}>
										Retry eligibility check
									</Button>
								</SpaceBetween>
							</Alert>
						)}
						{eligibilityQuery.data?.eligible === false && (
							<Alert type="warning">
								<SpaceBetween direction="vertical" size="s">
									<span>
										{eligibilityQuery.data.message ||
											'This Application is not eligible.'}
									</span>
									<Button onClick={() => void eligibilityQuery.refetch()}>
										Check again
									</Button>
									{eligibilityQuery.data.mongoDBID && (
										<Button
											onClick={() =>
												navigate(`/mongodb/${eligibilityQuery.data?.mongoDBID}`)
											}
										>
											Open existing request
										</Button>
									)}
								</SpaceBetween>
							</Alert>
						)}
						<ClusterConfiguration form={clusterForm} disabled={isSubmitting} />
						{isInheritedLoading && (
							<Alert type="info">Loading inherited Application details.</Alert>
						)}
						{applicationQuery.isError && (
							<Alert type="error">
								<SpaceBetween direction="vertical" size="s">
									<span>Unable to load the selected Application details.</span>
									<Button onClick={() => void applicationQuery.refetch()}>
										Retry
									</Button>
								</SpaceBetween>
							</Alert>
						)}
						<ApplicationDetails details={inheritedDetails} />
					</SpaceBetween>
				</Form>
			)}
		</SpaceBetween>
	)
}

export default NewMongoDBRequest
